import { CommonModule, KeyValue } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewChild, computed, inject, signal } from '@angular/core';
import { Observable, Subject, of } from 'rxjs';
import { catchError, finalize, map, takeUntil, tap } from 'rxjs/operators';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { saveAs } from 'file-saver';
import { SavedSearchesComponent } from './saved-searches/saved-searches.component';
import { AppService } from 'src/app/services/app.service';
import { MessageToUiService } from 'src/app/services/message-to-ui.service';
import { StatusesService } from 'src/app/services/statuses.service';
import { LocalizationService } from 'src/app/shared/localization/localization.service';
import { CaseStateFilter, CaseRoleName, IPaginatedResponse, IPaginatedSearch, IToolbarItemLink, KreditorReportType, WebUserRight } from 'src/app/shared/models';
import { ICustomCaseSearchOptions, CUSTOM_CASE_SEARCH_COLUMNS_TYPE, ICustomCaseSearchResult, ISavedCustomSearch, ICustomCaseListColumn } from '../reports-models';
import { ReportsService } from '../reports.service';
import { CaseOpenLocationService } from 'src/app/cases/case-open-location.service';
import { CaseSearchService } from 'src/app/services/case-search.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { CustomCaseListComponent } from './custom-case-list/custom-case-list.component';
import { RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { JsonDateParsePipe } from 'src/app/shared/pipes/json-date-parse.pipe';
import { CustomMultiSelectComponent } from 'src/app/shared/custom-multi-select/custom-multi-select.component';
import { CustomSelectComponent } from 'src/app/shared/custom-select/custom-select.component';
import { DatePickerModule } from 'primeng/datepicker';
import { GlobalSpinnerService } from 'src/app/shared/global-spinner/global-spinner.service';

@Component({
  selector: 'app-custom-reports',
  templateUrl: './custom-reports.component.html',
  styleUrls: ['./custom-reports.component.scss'],
  imports: [CommonModule, TranslateModule, CustomCaseListComponent, RouterModule, SavedSearchesComponent, FormsModule, JsonDateParsePipe, DatePickerModule,
    CustomMultiSelectComponent, CustomSelectComponent
  ]
})
export class CustomReportsComponent implements OnInit, OnDestroy {
  #spinnerService : GlobalSpinnerService = inject(GlobalSpinnerService)

  @ViewChild('savedSearchesComponent') savedSearchesComponent: SavedSearchesComponent;

  searchOptions = { caseSearchColumns: [], originalCustomers: [], customerNumbers: [], roleNames: [] } as ICustomCaseSearchOptions;

  filteredMenu$: Observable<IToolbarItemLink[]>;
  userPermissions$ = this.application.getPermissions();
  activeOrClosed: KeyValue<CaseStateFilter, string>[];
  caseRoles: KeyValue<CaseRoleName, string>[];
  companyOrPerson: KeyValue<string, string>[];
  flagged: KeyValue<boolean, string>[];
  untreatedTasks: KeyValue<boolean, string>[];
  customers$: Observable<KeyValue<number, string>[]>;
  originalCustomers$: Observable<KeyValue<string, string>[]>;
  caseStatuses$: Observable<KeyValue<number, string>[]>;
  searchResults$: Observable<IPaginatedResponse<ICustomCaseSearchResult>>;
  savedSearches: ISavedCustomSearch[];
  showLoading: boolean;
  pageSize = 20;
  currentPage = 1;
  localizationSettings$ = this.localizationService.settings$;
  customCaseListColumns: ICustomCaseListColumn[] = [];
  showResults: boolean;
  selectedSaveSearchId: number;
  selectedSaveSearchName: string;

  private readonly unsubscribe$ = new Subject();

  constructor(
    private application: AppService,
    private statusesService: StatusesService,
    private reportsService: ReportsService,
    private localizationService: LocalizationService,
    private notifications: MessageToUiService,
    private translateService: TranslateService,
    private openFromTracker: CaseOpenLocationService,
    private caseSearchService: CaseSearchService
  ) { }

  searchColumns = toSignal(this.application.getPermissions()
    .pipe(map(user => CUSTOM_CASE_SEARCH_COLUMNS_TYPE.filter(col => !(col as ICustomCaseListColumn).permissions?.length
      || (col as ICustomCaseListColumn).permissions.every(required => user.rights.includes(required))))));

  searchColumnsSplitIndex = computed(() => this.searchColumns() ? Math.ceil((this.searchColumns().length - 1) / 2) : 0)

  ngOnInit(): void {
    this.initializeDropdowns();
    this.reportsService.getSavedSearches().pipe(takeUntil(this.unsubscribe$)).subscribe(_ => this.savedSearches = _);

    this.application.getAvailableReports().subscribe(_ => {
      this.filterMenuAccess(_);
    });

    this.resumeIfNecessary();

    this.application.customer$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(_ => {
        const applicationCustomer = _?.number;
        if (applicationCustomer) { this.searchOptions.customerNumbers = [applicationCustomer]; }
      });
  }

  ngOnDestroy() {
    this.unsubscribe$.next(null);
    this.unsubscribe$.complete();
  }

  onChooseColumn(isChecked: boolean, column: ICustomCaseListColumn) {
    if (isChecked) {
      this.searchOptions.caseSearchColumns.push(column.name);
    }
    else {
      this.searchOptions.caseSearchColumns = this.searchOptions.caseSearchColumns.filter(x => x != column.name);
    }
  }

  onGenerateReport() {
    const paginatedSearch = structuredClone(this.constructSearchCriteria());
    this.customCaseListColumns = CUSTOM_CASE_SEARCH_COLUMNS_TYPE.filter(x => this.searchOptions.caseSearchColumns.includes(x.name));

    const indexClosingText = paginatedSearch.filter.caseSearchColumns.findIndex(x => x == "ClosingText");
    const indexStatusText = paginatedSearch.filter.caseSearchColumns.findIndex(x => x == "StatusText");

    if (indexClosingText >= 0 && this.translateService.currentLang.toLocaleLowerCase() == "en") {
      paginatedSearch.filter.caseSearchColumns[indexClosingText] = "ClosingTextEn";
    }

    if (indexStatusText >= 0 && this.translateService.currentLang.toLocaleLowerCase() == "en") {
      paginatedSearch.filter.caseSearchColumns[indexStatusText] = "StatusTextEn";
    }

    if (this.validateSearchOptions(paginatedSearch)) {
      this.showResults = true;
      this.showLoading = true;

      this.searchResults$ = this.caseSearchService.customCaseSearch(paginatedSearch).pipe(
        catchError(e => of({ totalCount: 0, pageItems: [] } as IPaginatedResponse<ICustomCaseSearchResult>)),
        tap(_ => {
          if (_ != null && _.totalCount == 0) {
            this.notifications.errorKey('Reports.NoCasesFound');
            this.showResults = false;
          }
          else if (paginatedSearch.filter.caseSearchColumns.find(x => x == 'DebtCollectionPhase')) {
            _.pageItems.map(x => x.debtCollectionPhase = this.translateService.instant(`Reports.DebtCollectionPhase.${x.debtCollectionPhase}`));
          }
        }),
        finalize(() => {
          this.showLoading = false;
          setTimeout(() => {
            this.scrollResultsIntoView();
          }, 200);
        }));
    }
  }

  onEmptySelection() {
    this.searchOptions = { caseSearchColumns: [] } as ICustomCaseSearchOptions;
    document.querySelectorAll('.column-checkbox').forEach(x => (x as HTMLInputElement).checked = false);
    this.searchResults$ = null;
    this.showResults = false;
    this.selectedSaveSearchId = null;
    this.savedSearchesComponent.onEmptySelection();
  }

  onClickSaveSearch() {
    if (!this.searchOptions.caseSearchColumns || this.searchOptions.caseSearchColumns.length == 0) {
      this.notifications.errorKey('Errors.Reports.NoColumnSelected')
      return;
    }
    this.savedSearchesComponent.onSaveSearch();
  }

  onSelectedPageChange(pageNumber: number): void {
    if (this.currentPage != pageNumber) {
      this.currentPage = pageNumber;
      this.onGenerateReport();
    }
  }

  onNumberOfItemsPerPageChange(numberOfItemsPerPage: number): void {
    this.pageSize = numberOfItemsPerPage;
    this.onGenerateReport();
  }

  onExportExcelClick(): void {
    const searchOptions = structuredClone(this.searchOptions);

    const indexClosingText = searchOptions.caseSearchColumns.findIndex(x => x == "ClosingText");
    const indexStatusText = searchOptions.caseSearchColumns.findIndex(x => x == "StatusText");

    if (indexClosingText >= 0 && this.translateService.currentLang.toLocaleLowerCase() == "en") {
      searchOptions.caseSearchColumns[indexClosingText] = "ClosingTextEn";
    }

    if (indexStatusText >= 0 && this.translateService.currentLang.toLocaleLowerCase() == "en") {
      searchOptions.caseSearchColumns[indexStatusText] = "StatusTextEn";
    }

    this.#spinnerService.show();
    this.reportsService.exportExcel(searchOptions, this.translateService.currentLang.toLocaleLowerCase())
      .pipe(finalize(() =>  this.#spinnerService.hide()),takeUntil(this.unsubscribe$))
      .subscribe({
        next: (blob) => {
        saveAs(blob, `${this.translateService.instant('CaseList.SearchResults') }_${ new Date().getDate() + (new Date().getMonth() + 1) + new Date().getFullYear() + '_' + new Date().getTime().toString().slice(0, 6) }.xlsx`);
        },
        error: () => {
          this.notifications.genericError();
        }
      });
  }

  resume(search: IPaginatedSearch<ICustomCaseSearchOptions>) {
    this.searchOptions = { ...search.filter };
    this.setFiltersAndColumnsFromSearchObject(this.searchOptions)

    this.pageSize = search.pageSize;
    this.currentPage = search.currentPage;

    this.onGenerateReport();
  }

  onSelectSavedSearch(savedSearch: ISavedCustomSearch) {
    this.searchOptions = { ...savedSearch.customSearch, caseSearchColumns: savedSearch.customSearch.caseSearchColumns.filter(c => this.searchColumns().find(_ => _.name === c)) };

    this.setFiltersAndColumnsFromSearchObject(savedSearch.customSearch);

    this.onGenerateReport();
  }

  private setFiltersAndColumnsFromSearchObject(search: ICustomCaseSearchOptions) {
    Object.keys(search).forEach(x => {
      if (search[x] && search[x] instanceof Date) {
        this.searchOptions[x] = new Date(search[x]);
      }
    });
    this.customCaseListColumns = CUSTOM_CASE_SEARCH_COLUMNS_TYPE.filter(x => search.caseSearchColumns.includes(x.name));
  }

  onSaveSearch(savedSearch: ISavedCustomSearch) {
    savedSearch.customSearch = this.searchOptions;

    if (!savedSearch.userPreferenceId) {
      this.reportsService.saveSearch(savedSearch).pipe(takeUntil(this.unsubscribe$))
        .subscribe(_ => {
          this.savedSearches.push(_);
          this.savedSearches = [...this.savedSearches];
        });
    }
    else {
      this.reportsService.updateSavedSearch(savedSearch.userPreferenceId, savedSearch).pipe(takeUntil(this.unsubscribe$))
        .subscribe(_ => {
          this.savedSearches[savedSearch.userPreferenceId] = _;
          this.savedSearches = [...this.savedSearches];
        });
    }
  }

  onDeleteSearch(userPreferenceId: number) {
    this.reportsService.deleteSavedSearch(userPreferenceId).pipe(takeUntil(this.unsubscribe$))
      .subscribe(_ => {
        this.savedSearches = [...this.savedSearches.filter(x => x.userPreferenceId != userPreferenceId)];
      });
  }


  onCaseSelected(caseModel: ICustomCaseSearchResult, list: IPaginatedResponse<ICustomCaseSearchResult>): void {
    this.openFromTracker.openCase({
      type: 'reports', caseNumberIndex: list.pageItems.indexOf(caseModel),
      filter: this.constructSearchCriteria(),
      pageItems: list.pageItems,
      totalCount: list.totalCount
    })
  }

  private constructSearchCriteria(): IPaginatedSearch<ICustomCaseSearchOptions> {
    return { pageSize: this.pageSize, currentPage: this.currentPage, filter: this.searchOptions } as IPaginatedSearch<ICustomCaseSearchOptions>;
  }

  private initializeDropdowns() {
    this.activeOrClosed = [
      { key: 'Active', value: 'Reports.Filters.Active' },
      { key: 'Closed', value: 'Reports.Filters.Closed' }
    ];

    this.companyOrPerson = [
      { key: 'false', value: 'Reports.Filters.Company' },
      { key: 'true', value: 'Reports.Filters.Person' }
    ];

    this.caseRoles = [
      { key: 'ReminderCase', value: 'Reports.Filters.Reminder' },
      { key: 'MainCase', value: 'Reports.Filters.Collection' },
      { key: 'Surveillance', value: 'Reports.Filters.Surveillance' }
    ];

    this.flagged = [
      { key: true, value: 'Reports.Filters.Yes' },
    ];

    this.untreatedTasks = [
      { key: true, value: 'Reports.Filters.Yes' },
    ];

    this.customers$ = this.application.customersSelectItems$;

    this.originalCustomers$ = this.application.customers$.pipe(map(_ => _?.map(x => ({ key: x.originalName, value: x.originalName }))
        .filter(x => x.key?.length)));

    this.caseStatuses$ = this.statusesService.getStatusGroups().pipe(map(_ => _.map(x => ({ key: x.id, value: x.translationTag } as KeyValue<number, string>))));
  }

  private validateSearchOptions(paginatedSearch: IPaginatedSearch<ICustomCaseSearchOptions>): boolean {
    if (!paginatedSearch.filter || !paginatedSearch.filter.caseSearchColumns || paginatedSearch.filter.caseSearchColumns.length == 0) {
      this.notifications.errorKey('Errors.Reports.NoColumnSelected');
      return false;
    }
    return true;
  }

  private scrollResultsIntoView() {
    document.querySelector('.results-container')?.scrollIntoView({ behavior: 'smooth', inline: 'start' });
  }

  private resumeIfNecessary() {

    const olderSearch = history.state.resume as IPaginatedSearch<ICustomCaseSearchOptions>;
    if (olderSearch)
      {this.resume(olderSearch); }
  }

  private filterMenuAccess(userAccessibleReports: KreditorReportType[]) {
    const defaultReports = ['CustomReports', 'PortfolioAnalysis', 'OutstandingAmount', 'Performance', 'ContractScore'];
    const allReports = [
      { translationTag: 'Reports.Performance.Title', link: '/reports/performance', name: 'Performance', id: 'performanceReportLink' } as IToolbarItemLink,
      { translationTag: 'Reports.PortfolioReport.Title', link: '/reports/portfolioAnalysis', name: 'PortfolioAnalysis', id: 'portfolioReportLink' } as IToolbarItemLink,
      { translationTag: 'Reports.ActiveCases.Title', link: '/reports/activeCases', name: 'ActiveCases', id: 'activeCasesReportLink' } as IToolbarItemLink,
      { translationTag: 'Reports.ClosedCases.Title', link: '/reports/closedCases', name: 'ClosedCases', id: 'closedCasesReportLink' } as IToolbarItemLink,
      { translationTag: 'Reports.TreatedCases.Title', link: '/reports/treatedCases', name: 'TreatedCases', id: 'treatedCasesReportLink' } as IToolbarItemLink,
      { translationTag: 'Reports.OutstandingAmount.Title', link: '/reports/outstandingAmount', name: 'OutstandingAmount', id: 'outstandingAmountReportLink' } as IToolbarItemLink,
      { translationTag: 'Reports.Status.Title', link: '/reports/status', name: 'Status', id: 'statusReportLink' } as IToolbarItemLink,
      { translationTag: 'Reports.ContractScore.Title', link: '/reports/contractScore', name: 'ContractScore', id: 'contractScoreReportLink', guard: { userRight: WebUserRight.AccessContractAdmin } } as IToolbarItemLink,
      { translationTag: 'Reports.Economic.Title', link: '/reports/economic', name: 'Economic', id: 'economicReportLink' } as IToolbarItemLink,
      { translationTag: 'Reports.SuccessRate.Title', link: '/reports/successRate', name: 'SuccessRate', id: 'successRateReportLink' } as IToolbarItemLink,
      { translationTag: 'Reports.ScoreReport.Title', link: '/reports/score', name: 'Score', id: 'scoreReportLink' } as IToolbarItemLink,
    ];

    this.filteredMenu$ = this.userPermissions$
    .pipe(map(userPermissions => {
      const userRights = userPermissions?.rights ?? [];

      return allReports.filter(x =>
        (defaultReports.includes(x.name) ||
          userAccessibleReports.some(y => y.label == x.name)) &&
        (x.guard?.userRight == null ||
          userRights.includes(x.guard.userRight)));
    }));
  }
}
